import React, { useEffect, useState, useRef } from 'react';
import { Button } from './Button';
import { ArrowLeftIcon, BookIcon, PlusIcon, CalendarIcon, SearchIcon, MoreHorizontalIcon, FilterIcon, TagIcon, MicIcon, PauseIcon, PlayIcon, TrashIcon, ImageIcon, CheckIcon, XIcon, BrainIcon, HeartIcon, ActivityIcon, ClockIcon, UserIcon } from 'lucide-react';
interface DiaryEntry {
  id: string;
  date: Date;
  title: string;
  content: string;
  tags: string[];
  mood?: string;
  type: 'text' | 'voice' | 'image' | 'check-in';
  audioUrl?: string;
  audioDuration?: number;
  imageUrl?: string;
  checkInData?: any; // For check-in entries
}
interface PersonalDiaryProps {
  onBack?: () => void;
}
const PersonalDiary: React.FC<PersonalDiaryProps> = ({
  onBack
}) => {
  const [activeView, setActiveView] = useState<'list' | 'entry' | 'create' | 'timeline'>('list');
  const [activeEntry, setActiveEntry] = useState<DiaryEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newEntry, setNewEntry] = useState({
    title: '',
    content: '',
    tags: [] as string[],
    type: 'text' as 'text' | 'voice' | 'image' | 'check-in'
  });
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Sample diary entries including check-ins
  const [entries, setEntries] = useState<DiaryEntry[]>([{
    id: '1',
    date: new Date(Date.now() - 86400000),
    title: 'Productieve dag',
    content: 'Vandaag voelde ik me gefocust en kon ik veel werk verzetten. De ademhalingsoefeningen van vanochtend hebben geholpen om kalm te blijven tijdens de presentatie.',
    tags: ['werk', 'focus'],
    mood: 'energiek',
    type: 'text'
  }, {
    id: '2',
    date: new Date(Date.now() - 86400000 * 3),
    title: 'Gesprek met Lisa',
    content: 'Had een goed gesprek met Lisa over onze plannen. Voelde me gehoord en begrepen. Moet vaker tijd maken voor zulke gesprekken.',
    tags: ['relaties', 'communicatie'],
    mood: 'tevreden',
    type: 'text'
  }, {
    id: 'check-1',
    date: new Date(Date.now() - 86400000 * 2),
    title: 'Ochtend check-in',
    content: 'Ik voel me vandaag redelijk uitgerust, maar wel wat gespannen over de deadline van morgen. Probeer stap voor stap te werken en niet te veel vooruit te denken.',
    tags: ['check-in', 'werk', 'stress'],
    mood: 'gespannen',
    type: 'check-in',
    checkInData: {
      mood: {
        x: 40,
        y: 30,
        label: 'Gespannen'
      },
      anxiety: 4,
      depression: 2,
      focus: 3,
      thoughts: 4,
      connection: 3,
      support: 4,
      sleep: 3,
      energy: 3,
      reflection: 'Ik voel me vandaag redelijk uitgerust, maar wel wat gespannen over de deadline van morgen. Probeer stap voor stap te werken en niet te veel vooruit te denken.'
    }
  }, {
    id: '3',
    date: new Date(Date.now() - 86400000 * 5),
    title: 'Gedachten over werk',
    content: '',
    tags: ['werk', 'reflectie'],
    mood: 'neutraal',
    type: 'voice',
    audioUrl: 'dummy-audio-url',
    audioDuration: 45 // seconds
  }, {
    id: 'check-2',
    date: new Date(Date.now() - 86400000 * 4),
    title: 'Avond check-in',
    content: 'Vandaag was een goede dag. De wandeling in het park heeft me geholpen om mijn gedachten te ordenen. Ik merk dat ik minder piekerde en meer in het moment was.',
    tags: ['check-in', 'ontspanning', 'mindfulness'],
    mood: 'ontspannen',
    type: 'check-in',
    checkInData: {
      mood: {
        x: 70,
        y: 60,
        label: 'Ontspannen'
      },
      anxiety: 2,
      depression: 1,
      focus: 4,
      thoughts: 2,
      connection: 3,
      support: 4,
      sleep: 4,
      energy: 4,
      reflection: 'Vandaag was een goede dag. De wandeling in het park heeft me geholpen om mijn gedachten te ordenen. Ik merk dat ik minder piekerde en meer in het moment was.'
    }
  }, {
    id: '4',
    date: new Date(Date.now() - 86400000 * 7),
    title: 'Moeite met slapen',
    content: 'Kon moeilijk in slaap komen. Gedachten bleven maar rondcirkelen. Heb de slaapoefening geprobeerd maar het hielp niet veel. Morgen eerder stoppen met schermtijd.',
    tags: ['slaap', 'stress'],
    mood: 'vermoeid',
    type: 'text'
  }]);
  useEffect(() => {
    // Clean up when component unmounts
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioURL) {
        URL.revokeObjectURL(audioURL);
      }
    };
  }, [audioURL]);
  const handleEntryClick = (entry: DiaryEntry) => {
    setActiveEntry(entry);
    setActiveView('entry');
  };
  const handleCreateNew = () => {
    setNewEntry({
      title: '',
      content: '',
      tags: [],
      type: 'text'
    });
    setAudioURL(null);
    setRecordingTime(0);
    setActiveView('create');
  };
  const handleSaveEntry = () => {
    // Create a new entry
    const newEntryObj: DiaryEntry = {
      id: Date.now().toString(),
      date: new Date(),
      title: newEntry.title || 'Notitie zonder titel',
      content: newEntry.content,
      tags: newEntry.tags,
      type: newEntry.type,
      mood: 'neutraal' // Default mood
    };
    // Add audio properties if it's a voice entry
    if (newEntry.type === 'voice' && audioURL) {
      newEntryObj.audioUrl = audioURL;
      newEntryObj.audioDuration = recordingTime;
    }
    // Add the new entry to the list
    setEntries([newEntryObj, ...entries]);
    setActiveView('list');
    // Show success feedback
    alert('Notitie opgeslagen');
  };
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      const audioChunks: BlobPart[] = [];
      mediaRecorder.ondataavailable = event => {
        audioChunks.push(event.data);
      };
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, {
          type: 'audio/wav'
        });
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
        // Create an audio element to test the recording
        if (audioRef.current) {
          audioRef.current.src = url;
        }
      };
      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
      // Start timer
      let seconds = 0;
      timerRef.current = setInterval(() => {
        seconds++;
        setRecordingTime(seconds);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Kon geen toegang krijgen tot de microfoon. Controleer je browserinstellingen.');
    }
  };
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      // Stop all tracks on the stream
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
  };
  const playAudio = (id: string, url: string) => {
    if (audioRef.current) {
      if (isPlaying && playingId === id) {
        // Pause if already playing this audio
        audioRef.current.pause();
        setIsPlaying(false);
        setPlayingId(null);
      } else {
        // Play new audio
        audioRef.current.src = url;
        audioRef.current.play();
        setIsPlaying(true);
        setPlayingId(id);
        // Add ended event listener
        audioRef.current.onended = () => {
          setIsPlaying(false);
          setPlayingId(null);
        };
      }
    }
  };
  const deleteRecording = () => {
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
      setAudioURL(null);
      setRecordingTime(0);
    }
  };
  // Filter entries based on activeFilter
  const filteredEntries = entries.filter(entry => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'check-ins') return entry.type === 'check-in';
    if (activeFilter === 'notes') return entry.type === 'text';
    if (activeFilter === 'voice') return entry.type === 'voice';
    return entry.tags.includes(activeFilter);
  });
  const renderListView = () => <div className="space-y-6" data-id="element-711">
      <div className="flex justify-between items-center mb-4" data-id="element-712">
        <div className="flex items-center" data-id="element-713">
          {onBack && <button onClick={onBack} className="mr-4 p-2 rounded-full hover:bg-gray-100 border border-gray-300" aria-label="Terug" data-id="element-714">
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-715" />
            </button>}
          <h1 className="text-2xl font-light text-gray-800" data-id="element-716">Dagboek</h1>
        </div>
        <div className="flex space-x-2" data-id="element-717">
          <button className={`p-2 rounded-full hover:bg-gray-100 border border-gray-300 ${activeView === 'timeline' ? 'bg-gray-200' : ''}`} aria-label="Tijdlijnweergave" onClick={() => setActiveView('timeline')} data-id="element-718">
            <CalendarIcon className="w-5 h-5 text-gray-600" data-id="element-719" />
          </button>
          <button className="p-2 rounded-full hover:bg-gray-100 border border-gray-300" aria-label="Filter" data-id="element-720">
            <FilterIcon className="w-5 h-5 text-gray-600" data-id="element-721" />
          </button>
        </div>
      </div>
      <div className="relative mb-6" data-id="element-722">
        <input type="text" placeholder="Zoek in je dagboek..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400" data-id="element-723" />
        <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" data-id="element-724" />
      </div>
      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4 overflow-x-auto" data-id="element-725">
        {['all', 'check-ins', 'notes', 'voice', 'stress', 'focus', 'slaap'].map(filter => <button key={filter} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border ${activeFilter === filter ? 'bg-gray-200 border-gray-400 text-gray-800' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`} onClick={() => setActiveFilter(filter)} data-id="element-726">
              {filter === 'all' && 'Alle'}
              {filter === 'check-ins' && 'Check-ins'}
              {filter === 'notes' && 'Notities'}
              {filter === 'voice' && 'Spraak'}
              {filter === 'stress' && 'Stress'}
              {filter === 'focus' && 'Focus'}
              {filter === 'slaap' && 'Slaap'}
            </button>)}
      </div>
      <div className="flex justify-end mb-4" data-id="element-727">
        <Button onClick={handleCreateNew} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-729" />} data-id="element-728">
          Nieuwe notitie
        </Button>
      </div>
      <div className="space-y-4" data-id="element-730">
        {filteredEntries.filter(entry => entry.title.toLowerCase().includes(searchQuery.toLowerCase()) || entry.content.toLowerCase().includes(searchQuery.toLowerCase()) || entry.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))).map(entry => <div key={entry.id} className={`p-4 border-2 ${entry.type === 'check-in' ? 'border-dashed' : 'border-solid'} border-gray-300 rounded-xl hover:bg-gray-50 cursor-pointer`} onClick={() => handleEntryClick(entry)} data-id="element-731">
              <div className="flex justify-between items-start" data-id="element-732">
                <div className="flex items-start" data-id="element-733">
                  <div className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center mr-3 bg-white" data-id="element-734">
                    {entry.type === 'check-in' ? <BrainIcon className="w-5 h-5 text-gray-600" data-id="element-735" /> : entry.type === 'voice' ? <MicIcon className="w-5 h-5 text-gray-600" data-id="element-736" /> : <BookIcon className="w-5 h-5 text-gray-600" data-id="element-737" />}
                  </div>
                  <div data-id="element-738">
                    <h3 className="text-lg font-medium text-gray-800 mb-1" data-id="element-739">
                      {entry.title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-2" data-id="element-740">
                      {formatDate(entry.date)}
                      {entry.mood && ` · ${entry.mood}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-741">
                  {entry.type === 'voice' && <button className="p-1 rounded-full hover:bg-gray-200 mr-2 border border-gray-200" onClick={e => {
              e.stopPropagation();
              if (entry.audioUrl) {
                playAudio(entry.id, entry.audioUrl);
              }
            }} aria-label={isPlaying && playingId === entry.id ? 'Pauzeren' : 'Afspelen'} data-id="element-742">
                      {isPlaying && playingId === entry.id ? <PauseIcon className="w-5 h-5 text-gray-600" data-id="element-743" /> : <PlayIcon className="w-5 h-5 text-gray-600" data-id="element-744" />}
                    </button>}
                  <button className="p-1 rounded-full hover:bg-gray-200 border border-gray-200" onClick={e => {
              e.stopPropagation();
              alert('Meer opties komen binnenkort beschikbaar');
            }} aria-label="Meer opties" data-id="element-745">
                    <MoreHorizontalIcon className="w-5 h-5 text-gray-600" data-id="element-746" />
                  </button>
                </div>
              </div>
              {(entry.type === 'text' || entry.type === 'check-in') && <p className="text-sm text-gray-600 mb-3 line-clamp-2 ml-13" data-id="element-747">
                  {entry.content}
                </p>}
              {entry.type === 'voice' && <div className="flex items-center mb-3 text-sm text-gray-600 ml-13" data-id="element-748">
                  <MicIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-749" />
                  <span data-id="element-750">
                    Spraakopname (
                    {entry.audioDuration && formatDuration(entry.audioDuration)}
                    )
                  </span>
                </div>}
              {entry.tags.length > 0 && <div className="flex flex-wrap gap-2 ml-13" data-id="element-751">
                  {entry.tags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-1 bg-gray-100 border border-gray-200 rounded-full text-xs text-gray-700" data-id="element-752">
                      <TagIcon className="w-3 h-3 mr-1" data-id="element-753" />
                      {tag}
                    </span>)}
                </div>}
            </div>)}
        {filteredEntries.length === 0 && <div className="p-8 text-center border-2 border-gray-300 border-dashed rounded-xl" data-id="element-754">
            <BookIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" data-id="element-755" />
            <h3 className="text-lg font-medium text-gray-700 mb-2" data-id="element-756">
              Nog geen notities
            </h3>
            <p className="text-sm text-gray-600 mb-4" data-id="element-757">
              Begin met het vastleggen van je gedachten en gevoelens
            </p>
            <Button onClick={handleCreateNew} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-759" />} data-id="element-758">
              Eerste notitie maken
            </Button>
          </div>}
      </div>
    </div>;
  const renderTimelineView = () => <div className="space-y-6" data-id="element-760">
      <div className="flex justify-between items-center mb-4" data-id="element-761">
        <div className="flex items-center" data-id="element-762">
          <button onClick={() => setActiveView('list')} className="mr-4 p-2 rounded-full hover:bg-gray-100 border border-gray-300" aria-label="Terug naar lijst" data-id="element-763">
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-764" />
          </button>
          <h1 className="text-2xl font-light text-gray-800" data-id="element-765">Tijdlijn</h1>
        </div>
        <div className="flex space-x-2" data-id="element-766">
          <button className="p-2 rounded-full hover:bg-gray-100 border border-gray-300" aria-label="Filter" data-id="element-767">
            <FilterIcon className="w-5 h-5 text-gray-600" data-id="element-768" />
          </button>
        </div>
      </div>
      <div className="flex justify-end mb-4" data-id="element-769">
        <Button onClick={handleCreateNew} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-771" />} data-id="element-770">
          Nieuwe notitie
        </Button>
      </div>
      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4 overflow-x-auto" data-id="element-772">
        {['all', 'check-ins', 'notes', 'voice', 'stress', 'focus', 'slaap'].map(filter => <button key={filter} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border ${activeFilter === filter ? 'bg-gray-200 border-gray-400 text-gray-800' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`} onClick={() => setActiveFilter(filter)} data-id="element-773">
              {filter === 'all' && 'Alle'}
              {filter === 'check-ins' && 'Check-ins'}
              {filter === 'notes' && 'Notities'}
              {filter === 'voice' && 'Spraak'}
              {filter === 'stress' && 'Stress'}
              {filter === 'focus' && 'Focus'}
              {filter === 'slaap' && 'Slaap'}
            </button>)}
      </div>
      {/* Group entries by date */}
      {Object.entries(filteredEntries.filter(entry => entry.title.toLowerCase().includes(searchQuery.toLowerCase()) || entry.content.toLowerCase().includes(searchQuery.toLowerCase()) || entry.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))).reduce((groups, entry) => {
      const date = entry.date.toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(entry);
      return groups;
    }, {} as Record<string, DiaryEntry[]>)).map(([date, dateEntries]) => <div key={date} className="mb-8" data-id="element-774">
          <h3 className="text-md font-medium text-gray-700 mb-4 sticky top-0 bg-white py-2 border-b border-gray-200" data-id="element-775">
            {date}
          </h3>
          <div className="space-y-4 pl-4 border-l-2 border-gray-200 border-dashed" data-id="element-776">
            {dateEntries.map(entry => <div key={entry.id} className="relative" data-id="element-777">
                <div className="absolute -left-[22px] top-4 w-4 h-4 rounded-full bg-white border-2 border-gray-400" data-id="element-778"></div>
                <div className={`ml-4 p-4 border ${entry.type === 'check-in' ? 'border-dashed' : 'border-solid'} border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer`} onClick={() => handleEntryClick(entry)} data-id="element-779">
                  <div className="flex justify-between items-start" data-id="element-780">
                    <div data-id="element-781">
                      <div className="flex items-center" data-id="element-782">
                        {entry.type === 'check-in' ? <BrainIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-783" /> : entry.type === 'voice' ? <MicIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-784" /> : <BookIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-785" />}
                        <p className="text-xs text-gray-500" data-id="element-786">
                          {formatTime(entry.date)}
                        </p>
                      </div>
                      <h4 className="text-base font-medium text-gray-800 mb-1 mt-1" data-id="element-787">
                        {entry.title}
                      </h4>
                    </div>
                    <div className="flex items-center" data-id="element-788">
                      {entry.type === 'voice' && <button className="p-1 rounded-full hover:bg-gray-200 mr-2 border border-gray-200" onClick={e => {
                  e.stopPropagation();
                  if (entry.audioUrl) {
                    playAudio(entry.id, entry.audioUrl);
                  }
                }} aria-label={isPlaying && playingId === entry.id ? 'Pauzeren' : 'Afspelen'} data-id="element-789">
                          {isPlaying && playingId === entry.id ? <PauseIcon className="w-4 h-4 text-gray-600" data-id="element-790" /> : <PlayIcon className="w-4 h-4 text-gray-600" data-id="element-791" />}
                        </button>}
                      <button className="p-1 rounded-full hover:bg-gray-200 border border-gray-200" onClick={e => {
                  e.stopPropagation();
                  alert('Meer opties komen binnenkort beschikbaar');
                }} aria-label="Meer opties" data-id="element-792">
                        <MoreHorizontalIcon className="w-5 h-5 text-gray-600" data-id="element-793" />
                      </button>
                    </div>
                  </div>
                  {(entry.type === 'text' || entry.type === 'check-in') && entry.content && <p className="text-sm text-gray-600 mt-2 line-clamp-3" data-id="element-794">
                        {entry.content}
                      </p>}
                  {entry.type === 'voice' && <div className="flex items-center mt-2 text-sm text-gray-600" data-id="element-795">
                      <MicIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-796" />
                      <span data-id="element-797">
                        Spraakopname (
                        {entry.audioDuration && formatDuration(entry.audioDuration)}
                        )
                      </span>
                    </div>}
                  {entry.tags.length > 0 && <div className="flex flex-wrap gap-2 mt-3" data-id="element-798">
                      {entry.tags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-1 bg-gray-100 border border-gray-200 rounded-full text-xs text-gray-700" data-id="element-799">
                          <TagIcon className="w-3 h-3 mr-1" data-id="element-800" />
                          {tag}
                        </span>)}
                    </div>}
                </div>
              </div>)}
          </div>
        </div>)}
    </div>;
  const renderEntryView = () => {
    if (!activeEntry) return null;
    const getEntryIcon = () => {
      switch (activeEntry.type) {
        case 'check-in':
          return <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-801" />;
        case 'voice':
          return <MicIcon className="w-6 h-6 text-gray-700" data-id="element-802" />;
        case 'image':
          return <ImageIcon className="w-6 h-6 text-gray-700" data-id="element-803" />;
        default:
          return <BookIcon className="w-6 h-6 text-gray-700" data-id="element-804" />;
      }
    };
    // For check-in entries, render the check-in data in a structured way
    const renderCheckInData = () => {
      if (activeEntry.type !== 'check-in' || !activeEntry.checkInData) return null;
      const data = activeEntry.checkInData;
      return <div className="space-y-4 mt-4 pt-4 border-t border-gray-200" data-id="element-805">
          <h3 className="text-base font-medium text-gray-700" data-id="element-806">
            Check-in details
          </h3>
          <div className="grid grid-cols-2 gap-4" data-id="element-807">
            <div className="p-3 border border-gray-200 rounded-lg" data-id="element-808">
              <div className="flex items-center mb-1" data-id="element-809">
                <BrainIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-810" />
                <span className="text-sm text-gray-600" data-id="element-811">Emotioneel</span>
              </div>
              <div className="space-y-2" data-id="element-812">
                <div data-id="element-813">
                  <p className="text-xs text-gray-500" data-id="element-814">Angst</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-815">
                    <div className="h-2 bg-gray-600 rounded-full" style={{
                    width: `${data.anxiety / 5 * 100}%`
                  }} data-id="element-816"></div>
                  </div>
                </div>
                <div data-id="element-817">
                  <p className="text-xs text-gray-500" data-id="element-818">Depressie</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-819">
                    <div className="h-2 bg-gray-600 rounded-full" style={{
                    width: `${data.depression / 5 * 100}%`
                  }} data-id="element-820"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-3 border border-gray-200 rounded-lg" data-id="element-821">
              <div className="flex items-center mb-1" data-id="element-822">
                <HeartIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-823" />
                <span className="text-sm text-gray-600" data-id="element-824">Mentaal</span>
              </div>
              <div className="space-y-2" data-id="element-825">
                <div data-id="element-826">
                  <p className="text-xs text-gray-500" data-id="element-827">Focus</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-828">
                    <div className="h-2 bg-gray-600 rounded-full" style={{
                    width: `${data.focus / 5 * 100}%`
                  }} data-id="element-829"></div>
                  </div>
                </div>
                <div data-id="element-830">
                  <p className="text-xs text-gray-500" data-id="element-831">Gedachten</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-832">
                    <div className="h-2 bg-gray-600 rounded-full" style={{
                    width: `${data.thoughts / 5 * 100}%`
                  }} data-id="element-833"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-3 border border-gray-200 rounded-lg" data-id="element-834">
              <div className="flex items-center mb-1" data-id="element-835">
                <UsersIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-836" />
                <span className="text-sm text-gray-600" data-id="element-837">Sociaal</span>
              </div>
              <div className="space-y-2" data-id="element-838">
                <div data-id="element-839">
                  <p className="text-xs text-gray-500" data-id="element-840">Verbinding</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-841">
                    <div className="h-2 bg-gray-600 rounded-full" style={{
                    width: `${data.connection / 5 * 100}%`
                  }} data-id="element-842"></div>
                  </div>
                </div>
                <div data-id="element-843">
                  <p className="text-xs text-gray-500" data-id="element-844">Steun</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-845">
                    <div className="h-2 bg-gray-600 rounded-full" style={{
                    width: `${data.support / 5 * 100}%`
                  }} data-id="element-846"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-3 border border-gray-200 rounded-lg" data-id="element-847">
              <div className="flex items-center mb-1" data-id="element-848">
                <ActivityIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-849" />
                <span className="text-sm text-gray-600" data-id="element-850">Fysiek</span>
              </div>
              <div className="space-y-2" data-id="element-851">
                <div data-id="element-852">
                  <p className="text-xs text-gray-500" data-id="element-853">Slaap</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-854">
                    <div className="h-2 bg-gray-600 rounded-full" style={{
                    width: `${data.sleep / 5 * 100}%`
                  }} data-id="element-855"></div>
                  </div>
                </div>
                <div data-id="element-856">
                  <p className="text-xs text-gray-500" data-id="element-857">Energie</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-858">
                    <div className="h-2 bg-gray-600 rounded-full" style={{
                    width: `${data.energy / 5 * 100}%`
                  }} data-id="element-859"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {data.mood && <div className="p-3 border border-gray-200 rounded-lg" data-id="element-860">
              <div className="flex items-center mb-1" data-id="element-861">
                <UserIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-862" />
                <span className="text-sm text-gray-600" data-id="element-863">Stemming</span>
              </div>
              <p className="text-sm text-gray-700" data-id="element-864">
                {data.mood.label || 'Niet gespecificeerd'}
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2" data-id="element-865">
                <div data-id="element-866">
                  <p className="text-xs text-gray-500" data-id="element-867">Energie</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-868">
                    <div className="h-2 bg-gray-600 rounded-full" style={{
                  width: `${100 - data.mood.y}%`
                }} data-id="element-869"></div>
                  </div>
                </div>
                <div data-id="element-870">
                  <p className="text-xs text-gray-500" data-id="element-871">Positiviteit</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-872">
                    <div className="h-2 bg-gray-600 rounded-full" style={{
                  width: `${data.mood.x}%`
                }} data-id="element-873"></div>
                  </div>
                </div>
              </div>
            </div>}
        </div>;
    };
    return <div className="space-y-6" data-id="element-874">
        <div className="flex items-center mb-4" data-id="element-875">
          <button onClick={() => setActiveView(activeView === 'timeline' ? 'timeline' : 'list')} className="mr-4 p-2 rounded-full hover:bg-gray-100 border border-gray-300" aria-label="Terug" data-id="element-876">
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-877" />
          </button>
          <h1 className="text-xl font-light text-gray-800" data-id="element-878">
            {activeEntry.type === 'check-in' ? 'Check-in bekijken' : 'Notitie bekijken'}
          </h1>
        </div>
        <div className={`p-6 border-2 ${activeEntry.type === 'check-in' ? 'border-dashed' : 'border-solid'} border-gray-300 rounded-xl`} data-id="element-879">
          <div className="flex justify-between items-start mb-4" data-id="element-880">
            <div className="flex items-start" data-id="element-881">
              <div className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center mr-4 bg-white" data-id="element-882">
                {getEntryIcon()}
              </div>
              <div data-id="element-883">
                <h2 className="text-xl font-medium text-gray-800 mb-1" data-id="element-884">
                  {activeEntry.title}
                </h2>
                <p className="text-sm text-gray-500" data-id="element-885">
                  {formatDate(activeEntry.date)} {formatTime(activeEntry.date)}
                  {activeEntry.mood && ` · ${activeEntry.mood}`}
                </p>
              </div>
            </div>
            <div className="flex space-x-1" data-id="element-886">
              <button className="p-2 rounded-full hover:bg-gray-100 border border-gray-200" aria-label="Bewerken" onClick={() => alert('Bewerken functie komt binnenkort')} data-id="element-887">
                <PlusIcon className="w-5 h-5 text-gray-600" data-id="element-888" />
              </button>
              <button className="p-2 rounded-full hover:bg-gray-100 border border-gray-200" aria-label="Meer opties" data-id="element-889">
                <MoreHorizontalIcon className="w-5 h-5 text-gray-600" data-id="element-890" />
              </button>
            </div>
          </div>
          {(activeEntry.type === 'text' || activeEntry.type === 'check-in') && <div className="mb-6" data-id="element-891">
              <p className="text-gray-700 whitespace-pre-line" data-id="element-892">
                {activeEntry.content}
              </p>
            </div>}
          {activeEntry.type === 'voice' && <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-893">
              <div className="flex items-center justify-between mb-2" data-id="element-894">
                <div className="flex items-center" data-id="element-895">
                  <MicIcon className="w-5 h-5 text-gray-600 mr-2" data-id="element-896" />
                  <span className="text-gray-700" data-id="element-897">Spraakopname</span>
                </div>
                <span className="text-sm text-gray-500" data-id="element-898">
                  {activeEntry.audioDuration && formatDuration(activeEntry.audioDuration)}
                </span>
              </div>
              <div className="flex justify-center mt-4" data-id="element-899">
                <button className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 border border-gray-300" onClick={() => {
              if (activeEntry.audioUrl) {
                playAudio(activeEntry.id, activeEntry.audioUrl);
              }
            }} data-id="element-900">
                  {isPlaying && playingId === activeEntry.id ? <PauseIcon className="w-6 h-6 text-gray-700" data-id="element-901" /> : <PlayIcon className="w-6 h-6 text-gray-700" data-id="element-902" />}
                </button>
              </div>
            </div>}
          {/* Render check-in data for check-in entries */}
          {activeEntry.type === 'check-in' && renderCheckInData()}
          {activeEntry.tags.length > 0 && <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200" data-id="element-903">
              {activeEntry.tags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-1 bg-gray-100 border border-gray-200 rounded-full text-xs text-gray-700" data-id="element-904">
                  <TagIcon className="w-3 h-3 mr-1" data-id="element-905" />
                  {tag}
                </span>)}
            </div>}
        </div>
        <div className="flex justify-between" data-id="element-906">
          <Button variant="secondary" onClick={() => setActiveView(activeView === 'timeline' ? 'timeline' : 'list')} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-908" />} data-id="element-907">
            Terug naar overzicht
          </Button>
          <Button onClick={() => alert('Functie komt binnenkort')} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-910" />} data-id="element-909">
            Toevoegen aan reflectie
          </Button>
        </div>
      </div>;
  };
  const renderCreateView = () => <div className="space-y-6" data-id="element-911">
      <div className="flex items-center mb-4" data-id="element-912">
        <button onClick={() => setActiveView('list')} className="mr-4 p-2 rounded-full hover:bg-gray-100 border border-gray-300" aria-label="Terug" data-id="element-913">
          <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-914" />
        </button>
        <h1 className="text-xl font-light text-gray-800" data-id="element-915">Nieuwe notitie</h1>
      </div>
      {/* Entry type selector */}
      <div className="flex border-b border-gray-200 mb-6" data-id="element-916">
        <button className={`px-4 py-2 text-sm font-medium transition-colors relative ${newEntry.type === 'text' ? 'text-gray-800 border-b-2 border-gray-700' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setNewEntry({
        ...newEntry,
        type: 'text'
      })} data-id="element-917">
          Tekst
        </button>
        <button className={`px-4 py-2 text-sm font-medium transition-colors relative ${newEntry.type === 'voice' ? 'text-gray-800 border-b-2 border-gray-700' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setNewEntry({
        ...newEntry,
        type: 'voice'
      })} data-id="element-918">
          Spraak
        </button>
        <button className={`px-4 py-2 text-sm font-medium transition-colors relative ${newEntry.type === 'image' ? 'text-gray-800 border-b-2 border-gray-700' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => {
        alert('Afbeeldingen toevoegen komt binnenkort beschikbaar');
        // setNewEntry({ ...newEntry, type: "image" })
      }} data-id="element-919">
          Afbeelding
        </button>
      </div>
      <div className="space-y-4" data-id="element-920">
        <div data-id="element-921">
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-922">
            Titel
          </label>
          <input id="title" type="text" value={newEntry.title} onChange={e => setNewEntry({
          ...newEntry,
          title: e.target.value
        })} placeholder="Geef je notitie een titel" className="w-full p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400" data-id="element-923" />
        </div>
        {newEntry.type === 'text' && <div data-id="element-924">
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-925">
              Inhoud
            </label>
            <textarea id="content" value={newEntry.content} onChange={e => setNewEntry({
          ...newEntry,
          content: e.target.value
        })} placeholder="Schrijf hier je gedachten..." className="w-full h-40 p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none" data-id="element-926" />
          </div>}
        {newEntry.type === 'voice' && <div className="p-4 border-2 border-gray-300 border-dashed rounded-lg" data-id="element-927">
            <div className="flex items-center justify-between mb-4" data-id="element-928">
              <div className="flex items-center" data-id="element-929">
                <MicIcon className="w-5 h-5 text-gray-600 mr-2" data-id="element-930" />
                <span className="text-gray-700" data-id="element-931">Spraakopname</span>
              </div>
              {isRecording && <span className="text-sm text-gray-600 flex items-center" data-id="element-932">
                  <div className="w-2 h-2 rounded-full bg-gray-600 mr-2 animate-pulse" data-id="element-933"></div>
                  {formatDuration(recordingTime)}
                </span>}
            </div>
            {!audioURL ? <div className="flex justify-center mt-4" data-id="element-934">
                {!isRecording ? <button className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 border border-gray-300" onClick={startRecording} data-id="element-935">
                    <MicIcon className="w-6 h-6 text-gray-700" data-id="element-936" />
                  </button> : <button className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 border border-gray-300" onClick={stopRecording} data-id="element-937">
                    <div className="w-6 h-6 text-gray-700 flex items-center justify-center" data-id="element-938">
                      <div className="w-4 h-4 bg-gray-700 rounded-sm" data-id="element-939"></div>
                    </div>
                  </button>}
              </div> : <div className="space-y-4" data-id="element-940">
                <div className="flex justify-center space-x-4" data-id="element-941">
                  <button className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 border border-gray-300" onClick={() => {
              if (audioURL) {
                playAudio('new', audioURL);
              }
            }} data-id="element-942">
                    {isPlaying && playingId === 'new' ? <PauseIcon className="w-6 h-6 text-gray-700" data-id="element-943" /> : <PlayIcon className="w-6 h-6 text-gray-700" data-id="element-944" />}
                  </button>
                  <button className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 border border-gray-300" onClick={deleteRecording} data-id="element-945">
                    <TrashIcon className="w-6 h-6 text-gray-700" data-id="element-946" />
                  </button>
                </div>
                <p className="text-center text-sm text-gray-600" data-id="element-947">
                  Opname ({formatDuration(recordingTime)})
                </p>
              </div>}
          </div>}
        {newEntry.type === 'image' && <div className="p-4 border-2 border-gray-300 border-dashed rounded-lg" data-id="element-948">
            <div className="flex items-center mb-4" data-id="element-949">
              <ImageIcon className="w-5 h-5 text-gray-600 mr-2" data-id="element-950" />
              <span className="text-gray-700" data-id="element-951">Afbeelding toevoegen</span>
            </div>
            <div className="flex justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg" data-id="element-952">
              <button className="flex flex-col items-center text-gray-500 hover:text-gray-700" onClick={() => alert('Afbeeldingen toevoegen komt binnenkort beschikbaar')} data-id="element-953">
                <ImageIcon className="w-12 h-12 mb-2" data-id="element-954" />
                <span data-id="element-955">Klik om een afbeelding te kiezen</span>
              </button>
            </div>
          </div>}
        <div data-id="element-956">
          <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-957">
            Tags (optioneel)
          </label>
          <div className="flex flex-wrap gap-2 p-3 border-2 border-gray-300 rounded-lg min-h-[80px]" data-id="element-958">
            {newEntry.tags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-1 bg-gray-100 border border-gray-200 rounded-full text-xs text-gray-700" data-id="element-959">
                <TagIcon className="w-3 h-3 mr-1" data-id="element-960" />
                {tag}
                <button className="ml-1 text-gray-500 hover:text-gray-700" onClick={() => setNewEntry({
              ...newEntry,
              tags: newEntry.tags.filter(t => t !== tag)
            })} data-id="element-961">
                  ×
                </button>
              </span>)}
            <input id="tags" type="text" placeholder="Voeg tags toe..." className="flex-grow min-w-[100px] outline-none border-none bg-transparent" onKeyDown={e => {
            if (e.key === 'Enter' && e.currentTarget.value.trim()) {
              e.preventDefault();
              const newTag = e.currentTarget.value.trim();
              if (!newEntry.tags.includes(newTag)) {
                setNewEntry({
                  ...newEntry,
                  tags: [...newEntry.tags, newTag]
                });
              }
              e.currentTarget.value = '';
            }
          }} data-id="element-962" />
          </div>
          <p className="text-xs text-gray-500 mt-1" data-id="element-963">
            Druk op Enter om een tag toe te voegen
          </p>
        </div>
      </div>
      <div className="flex justify-between" data-id="element-964">
        <Button variant="secondary" onClick={() => setActiveView('list')} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-966" />} data-id="element-965">
          Annuleren
        </Button>
        <Button onClick={handleSaveEntry} disabled={newEntry.type === 'voice' && !audioURL || newEntry.type === 'text' && !newEntry.content.trim()} data-id="element-967">
          Opslaan
        </Button>
      </div>
      {/* Hidden audio element for playback */}
      <audio ref={audioRef} className="hidden" data-id="element-968" />
    </div>;
  return <div className="w-full max-w-xl mx-auto py-8" data-id="element-969">
      {activeView === 'list' && renderListView()}
      {activeView === 'timeline' && renderTimelineView()}
      {activeView === 'entry' && renderEntryView()}
      {activeView === 'create' && renderCreateView()}
    </div>;
};
export default PersonalDiary;